// src/components/Sales/CreateSalePage.jsx
import React, { useState, useEffect } from 'react';
import createSaleService from '../../services/CreateSaleService';
import { showNotification } from '../../utils/notification';
import printBill from '../../utils/printBill';
import './Sales.css';

const MAIN_WAREHOUSE_ID = 1; // ← change to your main warehouse ID

const CreateSalePage = ({ toggleSidebar }) => {

  // ─── Data ──────────────────────────────────────────────────────────────────
  const [routes, setRoutes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingStock, setIsLoadingStock] = useState(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ─── Warehouse ─────────────────────────────────────────────────────────────
  const [selectedInventoryId, setSelectedInventoryId] = useState('');
  const [selectedInventoryName, setSelectedInventoryName] = useState('');

  // ─── Selections ────────────────────────────────────────────────────────────
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [selectedRouteName, setSelectedRouteName] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedCustomerName, setSelectedCustomerName] = useState('Cash Customer');
  const [isCashCustomer, setIsCashCustomer] = useState(true);

  // ─── Product search ────────────────────────────────────────────────────────
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  // ─── Product form ──────────────────────────────────────────────────────────
  const [productForm, setProductForm] = useState({
    categoryId: '',
    productId: '',
    qty: '1',
    unitPrice: '',
    availableQty: 0,
    stockId: ''
  });

  // ─── Cart ──────────────────────────────────────────────────────────────────
  const [cart, setCart] = useState([]);

  // ─── Return ────────────────────────────────────────────────────────────────
  const [returnAmount, setReturnAmount] = useState('');

  // ─── Payments ──────────────────────────────────────────────────────────────
  const [payments, setPayments] = useState([]);
  const [paymentForm, setPaymentForm] = useState({ type: 'CASH', amount: '' });

  // ─── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadRoutes();
    loadInvoiceNumber();
    loadMainWarehouse();
  }, []);

  const reloadAll = async () => {
    await Promise.all([
      loadRoutes(),
      loadInvoiceNumber(),
      loadMainWarehouse()
    ]);
  };

  // ─── Step 1: Load warehouse → then load categories ─────────────────────────
  const loadMainWarehouse = async () => {
    try {
      const inventories = await createSaleService.getActiveInventories();
      const main = inventories.find(inv => inv.id === MAIN_WAREHOUSE_ID) || inventories[0];
      if (main) {
        setSelectedInventoryId(String(main.id));
        setSelectedInventoryName(main.name);
        await loadCategories();
      }
    } catch (err) {
      console.error('Failed to load warehouse:', err);
    }
  };

  const loadCategories = async () => {
    try {
      setIsLoadingCategories(true);
      const data = await createSaleService.getAllCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const loadRoutes = async () => {
    try {
      const data = await createSaleService.getAvailableRoutes();
      setRoutes(data || []);
    } catch (err) {
      console.error('Failed to load routes:', err);
    }
  };

  const loadInvoiceNumber = async () => {
    try {
      const data = await createSaleService.getInvoiceNumber();
      setInvoiceNumber(data?.invoiceNumber || data?.data?.invoiceNumber || '');
    } catch (err) {
      console.error('Failed to load invoice number:', err);
    }
  };

  // ─── Route → customers ─────────────────────────────────────────────────────
  const handleRouteChange = async (routeId) => {
    const route = routes.find(r => String(r.id) === String(routeId));
    setSelectedRouteId(routeId);
    setSelectedRouteName(route?.name || '');
    setSelectedCustomerId('');
    setSelectedCustomerName('Cash Customer');
    setIsCashCustomer(true);
    setCustomers([]);
    if (!routeId) return;
    try {
      setIsLoadingCustomers(true);
      const data = await createSaleService.getCustomersByRoute(routeId);
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  // ─── Step 2: Category → load all products ─────────────────────────────────
  const handleCategoryChange = async (categoryId) => {
    setProductForm(prev => ({
      ...prev,
      categoryId,
      productId: '',
      qty: '1',
      unitPrice: '',
      availableQty: 0,
      stockId: ''
    }));
    setProducts([]);
    setProductSearch('');
    setShowProductDropdown(false);
    if (!categoryId) return;
    try {
      setIsLoadingProducts(true);
      const data = await createSaleService.getProductsByCategory(categoryId);
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // ─── Step 3: Product → load stock qty for this inventory ──────────────────
  const handleProductChange = async (productId, productName) => {
    setProductSearch(productName || '');
    setShowProductDropdown(false);
    setProductForm(prev => ({
      ...prev,
      productId,
      qty: '1',
      unitPrice: '',
      availableQty: 0,
      stockId: ''
    }));
    if (!productId || !selectedInventoryId) return;
    try {
      setIsLoadingStock(true);
      const stockData = await createSaleService.getStockByInventoryAndProduct(
        selectedInventoryId,
        productId
      );
      // stockData may be an array or single object depending on your API
      const stock = Array.isArray(stockData) ? stockData[0] : stockData;
      setProductForm(prev => ({
        ...prev,
        availableQty: stock?.qty ?? 0,
        unitPrice: stock?.pricePerUnit ? String(stock.pricePerUnit) : '',
        stockId: stock?.id ? String(stock.id) : ''
      }));
    } catch (err) {
      console.error('Failed to load stock:', err);
      setProductForm(prev => ({ ...prev, availableQty: 0, unitPrice: '', stockId: '' }));
    } finally {
      setIsLoadingStock(false);
    }
  };

  // ─── Cart operations ───────────────────────────────────────────────────────
  const addToCart = (e) => {
    e.preventDefault();
    if (!productForm.productId || !productForm.qty || !productForm.unitPrice) {
      showNotification('Please fill all product fields', 'error');
      return;
    }

    const requestedQty = parseInt(productForm.qty);
    if (requestedQty > productForm.availableQty) {
      showNotification(`Only ${productForm.availableQty} units available`, 'error');
      return;
    }

    const product = products.find(p => String(p.id) === String(productForm.productId));
    const category = categories.find(c => String(c.id) === String(productForm.categoryId));

    setCart(prev => [...prev, {
      type: 'sale',
      stockId: productForm.stockId,
      productId: productForm.productId,
      productName: product?.name || 'Unknown',
      categoryName: category?.name || '',
      qty: requestedQty,
      unitPrice: parseFloat(productForm.unitPrice),
      totalPrice: requestedQty * parseFloat(productForm.unitPrice)
    }]);

    setProductForm(prev => ({
      ...prev,
      productId: '',
      qty: '1',
      unitPrice: '',
      availableQty: 0,
      stockId: ''
    }));
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const removeFromCart = (index) => setCart(prev => prev.filter((_, i) => i !== index));

  // ─── Return operations ─────────────────────────────────────────────────────
  const addReturn = (e) => {
    e.preventDefault();
    const amt = parseFloat(returnAmount);
    if (isNaN(amt) || amt <= 0) {
      showNotification('Please enter a valid return amount', 'error');
      return;
    }

    const saleTotal = cart
      .filter(item => item.type === 'sale')
      .reduce((sum, item) => sum + item.totalPrice, 0);

    const existingReturn = cart.find(item => item.type === 'return');
    if (existingReturn) {
      showNotification('A return already exists. Remove it first to add a new one.', 'error');
      return;
    }

    if (amt > saleTotal) {
      showNotification('Return amount cannot exceed the sale total', 'error');
      return;
    }

    setCart(prev => [...prev, {
      type: 'return',
      productName: 'Sale Return (Deduction)',
      categoryName: '',
      qty: '-',
      unitPrice: 0,
      totalPrice: amt,
      amount: amt
    }]);

    setReturnAmount('');
  };

  // ─── Payment operations ────────────────────────────────────────────────────
  const addPayment = (e) => {
    e.preventDefault();
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      showNotification('Please enter a valid amount', 'error');
      return;
    }
    const newAmount = parseFloat(paymentForm.amount);
    setPayments(prev => {
      const existing = prev.find(p => p.type === paymentForm.type);
      if (existing) {
        return prev.map(p =>
          p.type === paymentForm.type
            ? { ...p, amount: p.amount + newAmount }
            : p
        );
      }
      return [...prev, { ...paymentForm, amount: newAmount }];
    });
    setPaymentForm(prev => ({ ...prev, amount: '' }));
  };

  const removePayment = (index) => setPayments(prev => prev.filter((_, i) => i !== index));

  // ─── Calculations ──────────────────────────────────────────────────────────
  const saleItems = cart.filter(item => item.type === 'sale');
  const returnItem = cart.find(item => item.type === 'return');
  const subTotal = saleItems.reduce((sum, item) => sum + item.totalPrice, 0);
  const returnTotal = returnItem ? returnItem.amount : 0;
  const grandTotal = subTotal - returnTotal;
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const balance = totalPaid - grandTotal;

  // ─── Reset ─────────────────────────────────────────────────────────────────
  const resetForm = () => {
    setSelectedRouteId('');
    setSelectedRouteName('');
    setSelectedCustomerId('');
    setSelectedCustomerName('Cash Customer');
    setIsCashCustomer(true);
    setProducts([]);
    setProductSearch('');
    setShowProductDropdown(false);
    setProductForm({ categoryId: '', productId: '', qty: '1', unitPrice: '', availableQty: 0, stockId: '' });
    setCart([]);
    setReturnAmount('');
    setPayments([]);
    setPaymentForm({ type: 'CASH', amount: '' });
    setCustomers([]);
    reloadAll();
  };

  // ─── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedRouteId) { showNotification('Please select a route', 'error'); return; }
    if (!isCashCustomer && !selectedCustomerId) { showNotification('Please select a customer', 'error'); return; }
    if (saleItems.length === 0) { showNotification('Please add items to cart', 'error'); return; }
    if (payments.length === 0) { showNotification('Please add at least one payment', 'error'); return; }
    if (totalPaid < grandTotal) { showNotification('Total paid must cover the grand total', 'error'); return; }

    const payload = {
      customerId: isCashCustomer ? null : parseInt(selectedCustomerId),
      routeId: parseInt(selectedRouteId),
      inventoryId: parseInt(selectedInventoryId),
      products: saleItems.map(item => ({
        productId: parseInt(item.productId),
        qty: item.qty,
        pricePerUnit: item.unitPrice
      })),
      paymentMethods: payments.map(p => ({
        method: p.type,
        amount: p.amount
      })),
      productreturnData: returnItem ? { amount: returnItem.amount } : null
    };

    // ── Print FIRST (before any await so browser allows the popup) ──
    printBill({
      invoiceNumber,
      cart: saleItems,
      returnAmount: returnTotal,
      payments,
      subTotal,
      grandTotal,
      totalPaid,
      balance,
      customerName: selectedCustomerName,
      inventoryName: selectedInventoryName,
      routeName: selectedRouteName
    });

    // ── Then save to API ──
    try {
      setIsSaving(true);
      await createSaleService.create(payload);
      showNotification('Sale created successfully!', 'success');
      resetForm();
    } catch (error) {
      console.error('Error saving sale:', error);
      showNotification(`Failed to save: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <main className="inventory-main-content">
      <header className="inventory-content-header">
        <button onClick={toggleSidebar} className="sidebar-toggle-btn" type="button">☰</button>
        <h2 className="inventory-content-title">
          Create Sale
          {invoiceNumber && (
            <span style={{ fontSize: '14px', fontWeight: 'normal', marginLeft: '16px', color: '#666' }}>
              #{invoiceNumber}
            </span>
          )}
        </h2>
      </header>

      <div style={{ display: 'flex', gap: '24px', padding: '16px', alignItems: 'flex-start' }}>

        {/* ── LEFT ── */}
        <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Warehouse + Route + Customer */}
          <div className="product-selection-card">
            <div className="form-row-sales">
              <div className="form-col-sales">
                <label>Warehouse</label>
                <input
                  className="custom-input"
                  value={isLoadingCategories ? 'Loading...' : selectedInventoryName || 'Loading...'}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'default' }}
                />
              </div>

              <div className="form-col-sales">
                <label>Route</label>
                <select
                  className="custom-select"
                  value={selectedRouteId}
                  onChange={e => handleRouteChange(e.target.value)}
                >
                  <option value="">Select Route</option>
                  {routes.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-col-sales">
                <label>Customer</label>
                <select
                  className="custom-select"
                  value={isCashCustomer ? 'cash' : selectedCustomerId}
                  onChange={e => {
                    if (e.target.value === 'cash') {
                      setIsCashCustomer(true);
                      setSelectedCustomerId('');
                      setSelectedCustomerName('Cash Customer');
                    } else {
                      const customer = customers.find(c => String(c.id) === e.target.value);
                      setIsCashCustomer(false);
                      setSelectedCustomerId(e.target.value);
                      setSelectedCustomerName(customer?.name || 'Unknown');
                    }
                  }}
                  disabled={isLoadingCustomers}
                >
                  <option value="cash">Cash Customer</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {!selectedRouteId && <small style={{ color: '#999' }}>Select a route first</small>}
                {isLoadingCustomers && <small style={{ color: '#999' }}>Loading...</small>}
              </div>
            </div>

            {/* Category + Product */}
            <div className="form-row-sales" style={{ marginTop: '12px' }}>
              <div className="form-col-sales">
                <label>Category</label>
                <select
                  className="custom-select"
                  value={productForm.categoryId}
                  onChange={e => handleCategoryChange(e.target.value)}
                  disabled={isLoadingCategories || categories.length === 0}
                >
                  <option value="">
                    {isLoadingCategories ? 'Loading...' : 'Select Category'}
                  </option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-col-sales" style={{ position: 'relative' }}>
                <label>Product</label>
                <input
                  type="text"
                  className="custom-input"
                  placeholder={
                    !productForm.categoryId
                      ? 'Select category first'
                      : isLoadingProducts
                      ? 'Loading...'
                      : 'Search product...'
                  }
                  value={productSearch}
                  disabled={!productForm.categoryId || isLoadingProducts}
                  onChange={e => {
                    setProductSearch(e.target.value);
                    setShowProductDropdown(true);
                    // clear selected product if user edits search
                    setProductForm(prev => ({ ...prev, productId: '', unitPrice: '', availableQty: 0, stockId: '' }));
                  }}
                  onFocus={() => { if (productForm.categoryId) setShowProductDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowProductDropdown(false), 150)}
                  autoComplete="off"
                />
                {showProductDropdown && products.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    maxHeight: '220px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    marginTop: '2px'
                  }}>
                    {products
                      .filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()))
                      .length === 0 ? (
                        <div style={{ padding: '10px 12px', color: '#999', fontSize: '13px' }}>
                          No products found
                        </div>
                      ) : (
                        products
                          .filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()))
                          .map(p => (
                            <div
                              key={p.id}
                              onMouseDown={() => handleProductChange(String(p.id), p.name)}
                              style={{
                                padding: '9px 12px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                borderBottom: '1px solid #f0f0f0',
                                backgroundColor: String(productForm.productId) === String(p.id) ? '#f0f7ff' : '#fff'
                              }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor =
                                String(productForm.productId) === String(p.id) ? '#f0f7ff' : '#fff'
                              }
                            >
                              {p.name}
                            </div>
                          ))
                      )
                    }
                  </div>
                )}
                {isLoadingStock && (
                  <small style={{ color: '#999' }}>Checking stock...</small>
                )}
                {productForm.productId && !isLoadingStock && (
                  <small className="availability-text">
                    Available: <strong>{productForm.availableQty}</strong> &nbsp;|&nbsp;
                    Unit Price: <strong>Rs. {productForm.unitPrice || '0'}</strong>
                  </small>
                )}
              </div>
            </div>

            {/* Qty + Price + Add */}
            <form onSubmit={addToCart}>
              <div className="form-row-sales" style={{ marginTop: '12px' }}>
                <div className="form-col-sales">
                  <label>Quantity</label>
                  <input
                    type="number"
                    className="custom-input"
                    value={productForm.qty}
                    onChange={e => setProductForm(prev => ({ ...prev, qty: e.target.value }))}
                    min="1"
                    max={productForm.availableQty}
                    disabled={!productForm.productId || isLoadingStock}
                    required
                  />
                </div>
                <div className="form-col-sales">
                  <label>Unit Price (Rs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="custom-input"
                    value={productForm.unitPrice}
                    onChange={e => setProductForm(prev => ({ ...prev, unitPrice: e.target.value }))}
                    disabled={!productForm.productId || isLoadingStock}
                    required
                  />
                </div>
                <div className="form-col-sales" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button
                    type="submit"
                    className="btn btn-add-item-full"
                    disabled={!productForm.productId || isLoadingStock}
                    style={{ width: '100%' }}
                  >
                    + Add to Cart
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Cart */}
          <div className="cart-display-card">
            <h4 className="card-title">Cart ({saleItems.length} items)</h4>
            {cart.length === 0 ? (
              <div className="empty-state">No items added yet.</div>
            ) : (
              <table className="mini-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Unit Price</th>
                    <th>Qty</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map((item, i) => (
                    <tr key={i} style={item.type === 'return' ? { backgroundColor: '#fff3f3', color: '#c0392b' } : {}}>
                      <td>{item.productName}</td>
                      <td>{item.categoryName}</td>
                      <td>{item.type === 'return' ? '-' : `Rs. ${item.unitPrice.toFixed(2)}`}</td>
                      <td>{item.qty}</td>
                      <td style={item.type === 'return' ? { color: '#c0392b', fontWeight: 'bold' } : {}}>
                        {item.type === 'return'
                          ? `- Rs. ${item.amount.toFixed(2)}`
                          : `Rs. ${item.totalPrice.toFixed(2)}`}
                      </td>
                      <td>
                        <button className="btn-remove-mini" onClick={() => removeFromCart(i)} type="button">×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Return */}
          <div className="product-selection-card">
            <h4 className="card-title" style={{ marginBottom: '10px' }}>
              Process Return
              {returnItem && (
                <span style={{ marginLeft: '12px', fontSize: '12px', color: '#c0392b', fontWeight: 'normal' }}>
                  ✓ Return of Rs. {returnItem.amount.toFixed(2)} applied
                </span>
              )}
            </h4>
            <form onSubmit={addReturn}>
              <div className="form-row-sales">
                <div className="form-col-sales">
                  <label>Return Amount (Rs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="custom-input"
                    value={returnAmount}
                    onChange={e => setReturnAmount(e.target.value)}
                    placeholder="Enter amount to deduct"
                    disabled={!!returnItem}
                  />
                  {returnItem && (
                    <small style={{ color: '#999' }}>Remove existing return to add a new one</small>
                  )}
                </div>
                <div className="form-col-sales" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button
                    type="submit"
                    className="btn btn-add-item-full"
                    disabled={!returnAmount || parseFloat(returnAmount) <= 0 || !!returnItem}
                    style={{ width: '100%', backgroundColor: '#e67e22', borderColor: '#e67e22' }}
                  >
                    Apply Return
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* ── RIGHT ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Payment */}
          <div className="payment-card">
            <h4 className="card-title">Payment</h4>

            <div className="payment-type-tabs">
              {['CASH', 'CHEQUE', 'CREDIT'].map(type => (
                <button
                  key={type}
                  type="button"
                  className={`payment-tab-btn ${paymentForm.type === type ? 'active' : ''}`}
                  onClick={() => setPaymentForm(prev => ({ ...prev, type }))}
                >
                  {type}
                </button>
              ))}
            </div>

            <form onSubmit={addPayment}>
              <div className="form-row-sales" style={{ marginTop: '10px' }}>
                <div className="form-col-sales">
                  <label>Amount (Rs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="custom-input"
                    value={paymentForm.amount}
                    onChange={e => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-col-sales btn-col">
                  <button type="submit" className="btn btn-add-payment">Add</button>
                </div>
              </div>
            </form>

            <div className="payments-list-section">
              {payments.length === 0 ? (
                <div className="empty-state">No payments added.</div>
              ) : (
                <table className="mini-table">
                  <thead>
                    <tr><th>Type</th><th>Amount</th><th></th></tr>
                  </thead>
                  <tbody>
                    {payments.map((p, i) => (
                      <tr key={i}>
                        <td>{p.type}</td>
                        <td>Rs. {p.amount.toFixed(2)}</td>
                        <td>
                          <button className="btn-remove-mini" onClick={() => removePayment(i)} type="button">×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Summary */}
            <div className="balance-summary">
              <div className="balance-row">
                <span>Sub Total:</span>
                <strong>Rs. {subTotal.toFixed(2)}</strong>
              </div>
              {returnTotal > 0 && (
                <div className="balance-row" style={{ color: '#c0392b' }}>
                  <span>Return Deduction:</span>
                  <strong>- Rs. {returnTotal.toFixed(2)}</strong>
                </div>
              )}
              <div className="balance-row" style={{ borderTop: '1px solid #eee', paddingTop: '6px', marginTop: '4px' }}>
                <span>Grand Total:</span>
                <strong>Rs. {grandTotal.toFixed(2)}</strong>
              </div>
              <div className="balance-row">
                <span>Total Paid:</span>
                <strong>Rs. {totalPaid.toFixed(2)}</strong>
              </div>
              <div className="balance-row">
                <span>{balance >= 0 ? 'Change:' : 'Still Owed:'}</span>
                <strong className={balance >= 0 ? 'positive' : 'negative'}>
                  Rs. {Math.abs(balance).toFixed(2)}
                </strong>
              </div>
            </div>
          </div>

          {/* Actions */}
          <button
            className="btn btn-save"
            onClick={handleSubmit}
            disabled={isSaving || saleItems.length === 0 || payments.length === 0}
            style={{ width: '100%', padding: '12px', fontSize: '15px' }}
          >
            {isSaving ? 'Processing...' : 'Complete Sale & Print'}
          </button>

          <button
            className="btn btn-cancel"
            onClick={resetForm}
            disabled={isSaving}
            style={{ width: '100%' }}
          >
            Clear / Reset
          </button>
        </div>
      </div>
    </main>
  );
};

export default CreateSalePage;
